import React, { useState, useEffect, useCallback } from 'react';
import { EntraIdConfig } from '../../types';
import { getAuthSettings, saveAuthSettings, getEntraDisplayNames } from '../../services/systemApi';

const AuthSettingsTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const [tenantId, setTenantId] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [hasExistingSecret, setHasExistingSecret] = useState(false);
  const [appDisplayName, setAppDisplayName] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data: EntraIdConfig = await getAuthSettings();
      setTenantId(data.tenantId || '');
      setClientId(data.clientId || '');
      // API never returns clientSecret; if tenantId exists, assume secret is configured
      setHasExistingSecret(!!(data.tenantId && data.clientId));
      setClientSecret('');
      if (data.tenantId && data.clientId) {
        getEntraDisplayNames()
          .then(r => setAppDisplayName(r.appDisplayName))
          .catch(e => console.warn('Could not fetch app display name:', e));
      }
    } catch (err: any) {
      setError(err.message || 'Unable to load authentication settings. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
    setValidationErrors({});
  };

  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!tenantId.trim()) {
      errors.tenantId = 'Tenant ID is required';
    }
    if (!clientId.trim()) {
      errors.clientId = 'Client ID is required';
    }
    if (!clientSecret && !hasExistingSecret) {
      errors.clientSecret = 'Client Secret is required';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    clearMessages();
    if (!validate()) return;

    setSaving(true);
    try {
      const config: EntraIdConfig = {
        tenantId: tenantId.trim(),
        clientId: clientId.trim(),
      };
      // Only include clientSecret if user entered a new one
      if (clientSecret) {
        config.clientSecret = clientSecret;
      }
      await saveAuthSettings(config);
      setSuccess('Authentication settings saved successfully.');
      setHasExistingSecret(true);
      setClientSecret('');
    } catch (err: any) {
      // Display validation errors from API
      if (err?.response?.status === 400) {
        const msg = err.response.data?.message || err.response.data?.error || 'Invalid configuration. Please check your inputs.';
        setError(msg);
      } else {
        setError(err.message || 'Failed to save authentication settings.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-gray-500">Loading authentication settings...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center justify-between" role="alert">
          <span>{error}</span>
          <button
            onClick={() => { setError(null); loadSettings(); }}
            className="ml-4 text-sm font-medium text-red-700 underline hover:text-red-900"
          >
            Retry
          </button>
        </div>
      )}

      {/* Success banner */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md flex items-center justify-between" role="status">
          <span>{success}</span>
          <button
            onClick={() => setSuccess(null)}
            className="ml-4 text-sm font-medium text-green-700 hover:text-green-900"
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      )}

      <h3 className="text-lg font-medium text-gray-900">Entra ID (Azure AD) Configuration</h3>

      <div className="bg-white shadow rounded-lg p-6">
        <div className="space-y-4 max-w-lg">
          {/* Tenant ID */}
          <div>
            <label htmlFor="tenant-id" className="block text-sm font-medium text-gray-700">
              Tenant ID
            </label>
            <input
              id="tenant-id"
              type="text"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                validationErrors.tenantId
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder="e.g. 12345678-abcd-1234-efgh-123456789012"
            />
            {validationErrors.tenantId && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.tenantId}</p>
            )}
          </div>

          {/* Client ID */}
          <div>
            <label htmlFor="client-id" className="block text-sm font-medium text-gray-700">
              Client ID
            </label>
            <input
              id="client-id"
              type="text"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                validationErrors.clientId
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder="e.g. abcdef12-3456-7890-abcd-ef1234567890"
            />
            {validationErrors.clientId && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.clientId}</p>
            )}
            {appDisplayName && (
              <p className="mt-1 text-sm text-gray-500">App name: <span className="font-medium text-gray-700">{appDisplayName}</span></p>
            )}
          </div>

          {/* Client Secret */}
          <div>
            <label htmlFor="client-secret" className="block text-sm font-medium text-gray-700">
              Client Secret
            </label>
            <input
              id="client-secret"
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              className={`mt-1 block w-full rounded-md shadow-sm sm:text-sm ${
                validationErrors.clientSecret
                  ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              placeholder={hasExistingSecret ? '••••••••••••••••' : 'Enter client secret'}
            />
            {validationErrors.clientSecret && (
              <p className="mt-1 text-sm text-red-600">{validationErrors.clientSecret}</p>
            )}
            {hasExistingSecret && !clientSecret && (
              <p className="mt-1 text-sm text-gray-500">
                Leave blank to keep the existing secret.
              </p>
            )}
          </div>
        </div>

        <div className="mt-6">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthSettingsTab;
